import { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { ExchangeGeneralContent } from '@ui/src';
import { useAppSelector } from '@store';
import {
  useLazyGetCustomersExchangeStatementQuery,
  useLazyGetCustomersAccountBalanceQuery,
} from '../store/services/customers';
import { Balance } from '../types';

const ProblemExchangeGeneralContentHOC = (): React.JSX.Element => {
  const { transactionId } = useParams();
  const [fromAccountBalance, setFromAccountBalance] = useState<number>();
  const [fromAccountBalances, setFromAccountBalances] = useState<Balance[]>([]);
  const [toAccountBalance, setToAccountBalance] = useState<number>();
  const [toAccountBalances, setToAccountBalances] = useState<Balance[]>([]);

  const {
    customersProblemTransactions: {
      exchange: { transaction },
    },
    customersDictionaries: { currenciesList, overdraftEnabled },
  } = useAppSelector((state) => state);

  const [getFromAccountBalance] = useLazyGetCustomersAccountBalanceQuery();
  const [getToAccountBalance] = useLazyGetCustomersAccountBalanceQuery();

  const getFromAccountBalanceAction = useCallback(async () => {
    const response = (await getFromAccountBalance({
      id: transaction?.senderAccount.id,
      currencyId: transaction?.exchangeCurrencyFromId as string,
    })) as { data: { data: { edges: { node: Balance }[] } } };

    if (response.data) {
      setFromAccountBalance(response.data.data.edges[0].node.value);
      setFromAccountBalances(
        response.data.data.edges?.map((item) => item.node),
      );
    }
  }, [transaction]);

  const getToAccountBalanceAction = useCallback(async () => {
    const response = (await getToAccountBalance({
      id: transaction?.recipientAccount.id,
      currencyId: transaction?.exchangeCurrencyToId as string,
    })) as { data: { data: { edges: { node: Balance }[] } } };

    if (response.data) {
      setToAccountBalance(response.data.data.edges[0].node.value);
      setToAccountBalances(response.data.data.edges?.map((item) => item.node));
    }
  }, [transaction]);

  const [getExchangeStatement, { isLoading }] =
    useLazyGetCustomersExchangeStatementQuery();

  const getExchangeStatementAction = useCallback(() => {
    getExchangeStatement(transactionId as string);
  }, [transactionId]);

  useEffect(() => {
    if (!transaction && transactionId) {
      getExchangeStatementAction();
    }
  }, [getExchangeStatementAction, transaction, transactionId]);

  useEffect(() => {
    if (transaction) {
      getFromAccountBalanceAction();
      getToAccountBalanceAction();
    }
  }, [transaction]);

  return (
    <ExchangeGeneralContent
      transactionInfo={transaction}
      fromAccountBalance={fromAccountBalance}
      toAccountBalance={toAccountBalance}
      currencyFromName={transaction?.exchangeCurrencyFrom?.name}
      currencyToName={transaction?.exchangeCurrencyTo?.name}
      senderAccountName={transaction?.senderAccount?.fullNumber}
      recipientAccountName={transaction?.recipientAccount?.fullNumber}
      rateSourceName={transaction?.exchangeRateSource?.name}
      isLoading={isLoading}
      exchangeType={transaction?.exchangeType}
      currenciesList={currenciesList}
      overdraftEnabled={overdraftEnabled}
      senderAccounts={[transaction?.senderAccount]}
      fromAccountBalances={fromAccountBalances}
      recipientAccounts={[transaction?.recipientAccount]}
      toAccountBalances={toAccountBalances}
    />
  );
};

export default ProblemExchangeGeneralContentHOC;
